// The full 78-card tarot deck (Rider–Waite–Smith structure).
//
// The `upright` / `reversed` one-liners are the FREE layer: they are shown to a
// visitor the moment their cards flip, before any login and without an LLM call.
// That is what makes the guest step feel like a real reading rather than a
// paywall — the interpretation that ties the three together is the paid/logged-in
// part.
//
// `id` is the contract with the server. It is stable and must never be renumbered:
// a stored reading references these ids, and the edge function validates against
// this same list. Names are display-only and can be re-worded freely.

export type TarotSuit = "wands" | "cups" | "swords" | "pentacles";

export interface TarotCard {
  id: string;
  name: string;
  arcana: "major" | "minor";
  suit?: TarotSuit;
  /** Display glyph in the corner: "0", "XIII", "A", "7", "Q"… */
  numeral: string;
  upright: string;
  reversed: string;
}

const major = (
  n: number,
  numeral: string,
  name: string,
  upright: string,
  reversed: string,
): TarotCard => ({
  id: `major-${String(n).padStart(2, "0")}`,
  name,
  arcana: "major",
  numeral,
  upright,
  reversed,
});

export const MAJOR_ARCANA: TarotCard[] = [
  major(0, "0", "The Fool", "A leap into something new, taken on faith", "Recklessness, or a leap you keep postponing"),
  major(1, "I", "The Magician", "You already hold every tool you need", "Talent scattered, or persuasion turned manipulative"),
  major(2, "II", "The High Priestess", "The answer is known to you but not yet spoken", "Ignoring an instinct you keep hearing"),
  major(3, "III", "The Empress", "Abundance, care, something growing under your hands", "Neglecting yourself while tending everyone else"),
  major(4, "IV", "The Emperor", "Structure, authority, boundaries that hold", "Control hardening into rigidity"),
  major(5, "V", "The Hierophant", "Tradition, mentorship, the tested path", "Rules you have outgrown"),
  major(6, "VI", "The Lovers", "A union, and a choice that defines you", "Misalignment of values, a decision avoided"),
  major(7, "VII", "The Chariot", "Momentum through sheer will", "Effort pulling in two directions at once"),
  major(8, "VIII", "Strength", "Quiet courage, patience over force", "Self-doubt, or force where softness was needed"),
  major(9, "IX", "The Hermit", "Withdraw to hear your own counsel", "Isolation that has stopped being useful"),
  major(10, "X", "Wheel of Fortune", "The cycle turns; timing shifts in your favour", "Resisting a change already underway"),
  major(11, "XI", "Justice", "Consequences land fairly; be accountable", "Imbalance, or a truth being dodged"),
  major(12, "XII", "The Hanged Man", "A pause that reveals another angle", "Stalling and calling it patience"),
  major(13, "XIII", "Death", "An ending that clears the ground", "Clinging to what has already finished"),
  major(14, "XIV", "Temperance", "Blend the extremes; find the middle rate", "Excess, impatience, doing too much at once"),
  major(15, "XV", "The Devil", "A pattern or attachment with a hold on you", "Beginning to loosen the chain"),
  major(16, "XVI", "The Tower", "Sudden upheaval that removes a false structure", "Disaster narrowly avoided, or delayed"),
  major(17, "XVII", "The Star", "Hope returns; you are being restored", "Faith thinning, inspiration hard to reach"),
  major(18, "XVIII", "The Moon", "Not everything here is as it appears", "Confusion clearing, a fear seen for what it is"),
  major(19, "XIX", "The Sun", "Clarity, warmth, visible success", "Optimism dimmed, or delayed gladness"),
  major(20, "XX", "Judgement", "A reckoning, and a call you must answer", "Self-criticism, or ignoring the summons"),
  major(21, "XXI", "The World", "Completion; the circle closes well", "A finish line moved, closure withheld"),
];

const RANKS: { numeral: string; label: string }[] = [
  { numeral: "A", label: "Ace" },
  { numeral: "2", label: "Two" },
  { numeral: "3", label: "Three" },
  { numeral: "4", label: "Four" },
  { numeral: "5", label: "Five" },
  { numeral: "6", label: "Six" },
  { numeral: "7", label: "Seven" },
  { numeral: "8", label: "Eight" },
  { numeral: "9", label: "Nine" },
  { numeral: "10", label: "Ten" },
  { numeral: "P", label: "Page" },
  { numeral: "N", label: "Knight" },
  { numeral: "Q", label: "Queen" },
  { numeral: "K", label: "King" },
];

const SUIT_LABEL: Record<TarotSuit, string> = {
  wands: "Wands",
  cups: "Cups",
  swords: "Swords",
  pentacles: "Pentacles",
};

/** 14 upright / reversed pairs per suit, in rank order Ace → King. */
const MINOR_MEANINGS: Record<TarotSuit, [string, string][]> = {
  wands: [
    ["A spark of inspiration worth acting on", "A false start, or an idea left unlit"],
    ["Planning the next move from a safe vantage", "Playing small, or a plan that never leaves paper"],
    ["Your effort is beginning to travel further", "Delays; the horizon is further than it looked"],
    ["A homecoming, a celebration, ground that holds", "Belonging that feels conditional right now"],
    ["Friendly conflict; competition sharpens you", "Friction with no purpose, energy wasted"],
    ["Recognition arrives; you are seen", "Doubt about whether the win was deserved"],
    ["Holding your position against pressure", "Overwhelmed by defending on every side"],
    ["Things move fast now; keep up", "Momentum stalls, messages cross"],
    ["Tired but still standing; nearly through", "Guardedness long after the threat has gone"],
    ["Carrying more than your share", "Time to set some of it down"],
    ["Curiosity and a message worth chasing", "Enthusiasm without follow-through"],
    ["Bold, headlong forward motion", "Haste creating the very obstacle you feared"],
    ["Warmth and confidence that draws people in", "Confidence performed rather than felt"],
    ["Vision others will follow", "Leading by force instead of example"],
  ],
  cups: [
    ["A new feeling opens; let it in", "Emotion held back, an offer declined"],
    ["Mutual attraction, a genuine meeting", "Imbalance between what each gives"],
    ["Friendship, community, shared gladness", "Overindulgence, or a circle that drains"],
    ["Discontent with what is already offered", "Apathy lifting; you notice the gift"],
    ["Grief for what spilled, but not all of it did", "Acceptance beginning, gaze lifting"],
    ["Nostalgia, an old kindness returning", "Living in a past that has moved on"],
    ["Many options, most of them illusions", "Clarity arrives; one path becomes real"],
    ["Walking away from what no longer nourishes", "Circling back to what you already left"],
    ["A wish that actually lands", "Satisfaction that turns out to be shallow"],
    ["Harmony that includes the people you love", "A picture of happiness that does not match inside"],
    ["A tender message, an unguarded feeling", "Sensitivity turning to sulking"],
    ["Following the heart, romance in motion", "Sweeping gestures without substance"],
    ["Intuition and compassion in balance", "Feelings managed by suppressing them"],
    ["Steady, generous emotional command", "Warmth withdrawn, moods ruling the room"],
  ],
  swords: [
    ["A cutting clarity; the truth is usable", "Confusion, or clarity used cruelly"],
    ["A decision refused; both eyes closed", "The blindfold lifts; you must now choose"],
    ["Heartbreak stated plainly", "Beginning to recover; the blade loosens"],
    ["Rest is not optional; retreat and mend", "Restlessness returning, ready to re-enter"],
    ["A win that costs more than it gives", "Reconciliation, or walking away wisely"],
    ["Moving away from trouble toward calmer water", "The crossing postponed; baggage still aboard"],
    ["Strategy, stealth, acting alone", "Getting caught, or returning what was taken"],
    ["Feeling trapped by circumstances of your own making", "The bindings are looser than they look"],
    ["Anxiety loudest at night; largely anticipatory", "The worst of the worry is passing"],
    ["A definitive, painful ending", "Slow recovery; the worst is behind"],
    ["Sharp curiosity, watching and learning", "Gossip, or knowledge used carelessly"],
    ["Charging in with conviction", "Aggression without direction"],
    ["Clear-eyed judgement, unsentimental", "Coldness where kindness was needed"],
    ["Authority through truth and reason", "Judgement turned harsh and absolute"],
  ],
  pentacles: [
    ["A tangible opportunity; something real begins", "An opening missed, or bad timing"],
    ["Juggling demands with some grace", "One ball too many in the air"],
    ["Skill recognised; collaboration works", "Working alone when help was available"],
    ["Holding on tightly to what you have", "Loosening the grip; generosity returns"],
    ["Hardship, and help closer than you think", "Recovery begins; the door opens"],
    ["Giving and receiving in fair measure", "Strings attached to the gift"],
    ["A pause to assess what your effort has grown", "Impatience with a slow harvest"],
    ["Craft, repetition, getting genuinely good", "Going through the motions"],
    ["Self-sufficiency; enjoying what you built", "Comfort bought at the cost of connection"],
    ["Security that outlasts you; family and legacy", "Short-term thinking, an unstable base"],
    ["A student's appetite; a practical new start", "Distraction, plans left unstudied"],
    ["Slow, dependable, unglamorous progress", "Stagnation, or stubborn refusal to adapt"],
    ["Nurturing prosperity, practical and warm", "Overwork crowding out everything else"],
    ["Mastery of the material world", "Wealth defended rather than enjoyed"],
  ],
};

export const MINOR_ARCANA: TarotCard[] = (
  Object.keys(MINOR_MEANINGS) as TarotSuit[]
).flatMap((suit) =>
  RANKS.map((rank, i) => {
    const [upright, reversed] = MINOR_MEANINGS[suit][i];
    return {
      id: `${suit}-${String(i + 1).padStart(2, "0")}`,
      name: `${rank.label} of ${SUIT_LABEL[suit]}`,
      arcana: "minor" as const,
      suit,
      numeral: rank.numeral,
      upright,
      reversed,
    };
  }),
);

export const TAROT_DECK: TarotCard[] = [...MAJOR_ARCANA, ...MINOR_ARCANA];

export const CARD_BY_ID: Record<string, TarotCard> = Object.fromEntries(
  TAROT_DECK.map((c) => [c.id, c]),
);

export const POSITIONS = ["Past", "Present", "Future"] as const;
export type TarotPosition = (typeof POSITIONS)[number];

/** One drawn card: which card, and which way up. */
export interface DrawnCard {
  id: string;
  reversed: boolean;
}

/**
 * Draw three distinct cards using the browser's CSPRNG.
 *
 * Math.random() is seeded per-tab and is trivially predictable, which for a
 * "your cards" experience is the wrong feel; crypto costs nothing here. Rejection
 * sampling on a modulo-free range keeps every card equally likely (a naive
 * `% 78` would bias the first 22 cards).
 */
export function drawThree(): DrawnCard[] {
  const n = TAROT_DECK.length;
  const limit = Math.floor(0xffffffff / n) * n; // reject above this to kill modulo bias
  const picked: DrawnCard[] = [];
  const seen = new Set<number>();
  const buf = new Uint32Array(1);

  while (picked.length < 3) {
    crypto.getRandomValues(buf);
    if (buf[0] >= limit) continue;
    const idx = buf[0] % n;
    if (seen.has(idx)) continue;
    seen.add(idx);
    crypto.getRandomValues(buf);
    picked.push({ id: TAROT_DECK[idx].id, reversed: buf[0] % 2 === 0 });
  }
  return picked;
}

if (process.env.NODE_ENV === "development" && TAROT_DECK.length !== 78) {
  // eslint-disable-next-line no-console
  console.error(`[tarot] deck is ${TAROT_DECK.length} cards, expected 78`);
}
